import React, { useEffect, useRef, useState } from 'react';
import { HiOutlineX } from 'react-icons/hi';

const VIEW_WIDTH = 272;
const VIEW_HEIGHT = 308;
const OUTPUT_WIDTH = 750;
const OUTPUT_HEIGHT = 850;
const MIN_ZOOM = 1;
const MAX_ZOOM = 3;

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

/**
 * 조절 화면은 원형 아바타와 같게 보여주고, 저장은 기존처럼 7.5:8.5(750×850)로 크롭한다.
 */
const AvatarCropModal = ({ open, file, onCancel, onConfirm }) => {
  const imgRef = useRef(null);
  const dragRef = useRef(null);
  const [imageUrl, setImageUrl] = useState('');
  const [naturalSize, setNaturalSize] = useState({ width: 0, height: 0 });
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });

  useEffect(() => {
    if (!open || !file) {
      setImageUrl('');
      return undefined;
    }
    const url = URL.createObjectURL(file);
    setImageUrl(url);
    setZoom(1);
    setOffset({ x: 0, y: 0 });
    return () => URL.revokeObjectURL(url);
  }, [open, file]);

  const coverScale = (() => {
    if (!naturalSize.width || !naturalSize.height) return 1;
    return Math.max(VIEW_WIDTH / naturalSize.width, VIEW_HEIGHT / naturalSize.height);
  })();

  const displayWidth = naturalSize.width * coverScale * zoom;
  const displayHeight = naturalSize.height * coverScale * zoom;

  const maxOffsetX = Math.max(0, (displayWidth - VIEW_WIDTH) / 2);
  const maxOffsetY = Math.max(0, (displayHeight - VIEW_HEIGHT) / 2);

  useEffect(() => {
    setOffset((prev) => {
      const next = {
        x: clamp(prev.x, -maxOffsetX, maxOffsetX),
        y: clamp(prev.y, -maxOffsetY, maxOffsetY),
      };
      return next.x === prev.x && next.y === prev.y ? prev : next;
    });
  }, [maxOffsetX, maxOffsetY]);

  const handleImageLoad = (event) => {
    setNaturalSize({
      width: event.target.naturalWidth,
      height: event.target.naturalHeight,
    });
  };

  const applyOffset = (nextX, nextY) => {
    setOffset({
      x: clamp(nextX, -maxOffsetX, maxOffsetX),
      y: clamp(nextY, -maxOffsetY, maxOffsetY),
    });
  };

  const handlePointerDown = (event) => {
    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    dragRef.current = {
      startX: event.clientX,
      startY: event.clientY,
      originX: offset.x,
      originY: offset.y,
    };
  };

  const handlePointerMove = (event) => {
    if (!dragRef.current) return;
    const dx = event.clientX - dragRef.current.startX;
    const dy = event.clientY - dragRef.current.startY;
    applyOffset(dragRef.current.originX + dx, dragRef.current.originY + dy);
  };

  const handlePointerUp = () => {
    dragRef.current = null;
  };

  const handleWheel = (event) => {
    event.preventDefault();
    const nextZoom = clamp(zoom + (event.deltaY < 0 ? 0.1 : -0.1), MIN_ZOOM, MAX_ZOOM);
    setZoom(nextZoom);
  };

  const handleConfirm = () => {
    const image = imgRef.current;
    if (!image || !naturalSize.width) return;
    const canvas = document.createElement('canvas');
    canvas.width = OUTPUT_WIDTH;
    canvas.height = OUTPUT_HEIGHT;
    const ctx = canvas.getContext('2d');
    const scale = (coverScale * zoom * OUTPUT_WIDTH) / VIEW_WIDTH;
    const drawWidth = naturalSize.width * scale;
    const drawHeight = naturalSize.height * scale;
    const dx = (OUTPUT_WIDTH - drawWidth) / 2 + (offset.x * OUTPUT_WIDTH) / VIEW_WIDTH;
    const dy = (OUTPUT_HEIGHT - drawHeight) / 2 + (offset.y * OUTPUT_HEIGHT) / VIEW_HEIGHT;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, OUTPUT_WIDTH, OUTPUT_HEIGHT);
    ctx.drawImage(image, dx, dy, drawWidth, drawHeight);
    canvas.toBlob(
      (blob) => {
        if (!blob) return;
        const cropped = new File([blob], 'avatar.jpg', { type: 'image/jpeg' });
        onConfirm(cropped);
      },
      'image/jpeg',
      0.9
    );
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onCancel} />
      <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
          <h4 className="text-base font-semibold text-gray-900">프로필 사진 위치 조절</h4>
          <button type="button" onClick={onCancel} className="p-1 text-gray-500 hover:text-gray-700">
            <HiOutlineX className="w-5 h-5" />
          </button>
        </div>
        <div className="p-4 space-y-4">
          <p className="text-xs text-gray-500">드래그로 위치를 맞추고, 슬라이더로 확대·축소하세요.</p>
          <div
            className="relative mx-auto overflow-hidden rounded-full bg-gray-100 ring-1 ring-gray-200 touch-none"
            style={{ width: VIEW_WIDTH, height: VIEW_WIDTH }}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerCancel={handlePointerUp}
            onWheel={handleWheel}
          >
            {imageUrl && (
              <img
                ref={imgRef}
                src={imageUrl}
                alt="자를 프로필 사진"
                onLoad={handleImageLoad}
                draggable={false}
                className="absolute select-none pointer-events-none max-w-none"
                style={{
                  width: displayWidth || 'auto',
                  height: displayHeight || 'auto',
                  left: '50%',
                  top: '50%',
                  transform: `translate(calc(-50% + ${offset.x}px), calc(-50% + ${offset.y}px))`,
                }}
              />
            )}
          </div>
          <label className="block text-xs font-medium text-gray-500">
            확대
            <input
              type="range"
              min={MIN_ZOOM}
              max={MAX_ZOOM}
              step="0.05"
              value={zoom}
              onChange={(e) => setZoom(Number(e.target.value))}
              className="mt-1 w-full"
            />
          </label>
        </div>
        <div className="px-4 py-3 border-t border-gray-200 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            취소
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700"
          >
            적용
          </button>
        </div>
      </div>
    </div>
  );
};

export default AvatarCropModal;
